/**
 * leadInformationPrefill — derives best-effort field values for the
 * Lead-Focus → Information tab from whatever's already on the demo lead
 * (NPPES/CMS data, CSV imports, or hand-seeded demo records).
 *
 * Two layers:
 *  1. Pass-through  — fields the source data already supplies (e.g. specialty,
 *     phone, stage). No badge.
 *  2. AI-inferred   — fields we can confidently derive (e.g. Time Zone from
 *     state, Practice Type from `practiceType`, Lead Rating from AI score).
 *     These come with a parallel `<field>_ai = true` flag the Information tab
 *     UI can use to render a ✦ AI badge next to the value.
 *
 * Pure / synchronous. Safe to call inside React renders.
 */

// State → US timezone (rough — assumes the practice's primary office)
const STATE_TZ = {
  AL: "America/Chicago", AK: "America/Anchorage", AZ: "America/Phoenix",
  AR: "America/Chicago", CA: "America/Los_Angeles", CO: "America/Denver",
  CT: "America/New_York", DE: "America/New_York", FL: "America/New_York",
  GA: "America/New_York", HI: "Pacific/Honolulu", ID: "America/Boise",
  IL: "America/Chicago", IN: "America/Indianapolis", IA: "America/Chicago",
  KS: "America/Chicago", KY: "America/New_York", LA: "America/Chicago",
  ME: "America/New_York", MD: "America/New_York", MA: "America/New_York",
  MI: "America/Detroit", MN: "America/Chicago", MS: "America/Chicago",
  MO: "America/Chicago", MT: "America/Denver", NE: "America/Chicago",
  NV: "America/Los_Angeles", NH: "America/New_York", NJ: "America/New_York",
  NM: "America/Denver", NY: "America/New_York", NC: "America/New_York",
  ND: "America/Chicago", OH: "America/New_York", OK: "America/Chicago",
  OR: "America/Los_Angeles", PA: "America/New_York", RI: "America/New_York",
  SC: "America/New_York", SD: "America/Chicago", TN: "America/Chicago",
  TX: "America/Chicago", UT: "America/Denver", VT: "America/New_York",
  VA: "America/New_York", WA: "America/Los_Angeles", WV: "America/New_York",
  WI: "America/Chicago", WY: "America/Denver", DC: "America/New_York",
};

const STAGE_NORM = {
  new_leads: "New Lead – Immediate Follow-Up",
  contacted: "Contacted",
  demo: "Demo Scheduled",
  demo_completed: "Demo Completed",
  proposal: "Proposal Sent",
  negotiation: "Contract Sent",
  contract: "Contract Signed",
  active: "Active Client",
  closed_lost: "Closed Lost",
  qualified: "Qualified",
};

const RATING_NORM = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (s.includes("hot")) return "Hot";
  if (s.includes("warm")) return "Warm";
  if (s.includes("cold")) return "Cold";
  if (s.includes("dormant") || s.includes("dead")) return "Dormant";
  return null;
};

const SENTIMENT_NORM = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (s.includes("positive")) return "Positive";
  if (s.includes("negative")) return "Negative";
  if (s.includes("mixed")) return "Mixed";
  if (s.includes("neutral")) return "Neutral";
  return null;
};

/**
 * @param {object} lead - the demo or real lead object
 * @returns {object} merged lead with prefilled fields + parallel `<field>_ai` flags
 */
export function prefillLeadInformation(lead) {
  if (!lead || typeof lead !== "object") return lead;
  const out = { ...lead };
  const setAI = (key, val) => {
    if (out[key] == null || out[key] === "") {
      out[key] = val;
      out[`${key}_ai`] = true;
    }
  };
  const setPass = (key, val) => {
    if ((out[key] == null || out[key] === "") && val != null && val !== "") {
      out[key] = val;
    }
  };

  // ── Section 1 — Contact & Practice Identity ──
  const primary = Array.isArray(lead.contacts) ? lead.contacts.find(c => c.primary) || lead.contacts[0] : null;
  if (primary) {
    setPass("contact_name", primary.name);
    setPass("phone", primary.phone);
    setPass("email", primary.email);
    setPass("organizational_role", primary.role);
  }
  setPass("company_name", lead.clinic || lead.company || lead.practice_name);
  setPass("specialty", lead.specialty);
  setPass("practice_type", lead.practiceType || lead.practice_type);
  setPass("provider_count", lead.providers || lead.provider_count);
  setPass("npi", lead.npi);

  // Address from locations[0]
  const loc = Array.isArray(lead.locations) ? lead.locations.find(l => l.primary) || lead.locations[0] : null;
  if (loc) {
    setPass("address_street", loc.address);
    setPass("address_city", loc.city);
    setPass("address_state", loc.state);
    setPass("address_zip", loc.zip);
  }
  // AI-inferred timezone from state
  if (out.address_state && STATE_TZ[String(out.address_state).toUpperCase()]) {
    setAI("time_zone", STATE_TZ[String(out.address_state).toUpperCase()]);
  }

  // ── Section 2 — Pipeline & Lead Status ──
  if (lead.stage) {
    const normStage = STAGE_NORM[String(lead.stage).toLowerCase()] || lead.stage;
    if (normStage !== lead.stage) setAI("lead_stage", normStage);
    else setPass("lead_stage", normStage);
  }
  setPass("sub_status", lead.subStatus || lead.sub_status);
  setPass("lead_source", lead.lead_source || lead.source);
  const rating = RATING_NORM(lead.rating || lead.lead_rating);
  if (rating && (out.lead_rating == null || out.lead_rating === "")) setAI("lead_rating", rating);
  // AI score → mission-critical fallback. Accepts both legacy int and
  // the new engine dict shape via the shared `getLeadScore` helper, so
  // dict-shaped scores from the AI Lead Targeting engine still drive
  // the rating bucket here.
  const __aiScoreNum = (typeof lead.ai_score === "number")
    ? lead.ai_score
    : (lead.ai_score && typeof lead.ai_score === "object" ? Number(lead.ai_score?.primary?.score) || 0 : 0);
  if (__aiScoreNum > 0 && out.lead_rating == null) {
    if (__aiScoreNum >= 71) setAI("lead_rating", "Hot");
    else if (__aiScoreNum >= 41) setAI("lead_rating", "Warm");
    else setAI("lead_rating", "Cold");
  }
  setPass("pipeline", lead.pipeline);

  // ── Section 3 — Products & Qualification ──
  setPass("products_of_interest", Array.isArray(lead.products_of_interest) ? lead.products_of_interest : (lead.products_of_interest ? [lead.products_of_interest] : null));
  setPass("feature_interest_category", lead.feature_interest_category);
  setPass("key_value_proposition", lead.key_value_proposition);
  // AI-inferred decision timeline from stage
  if (lead.stage === "proposal" || lead.stage === "negotiation") setAI("prospect_decision_timeline", "Active negotiation (< 30 days)");
  else if (lead.stage === "demo" || lead.stage === "demo_completed") setAI("prospect_decision_timeline", "Evaluating (30–60 days)");

  // ── Section 4 — Budget & Deal Details ──
  setPass("budget_range", lead.budget_range || lead.budget);
  setPass("pricing_fit_status", lead.pricing_fit_status);
  setPass("est_deal_value", lead.est_deal_value || lead.deal_value);
  setPass("key_decision_makers_identified", lead.key_decision_makers || lead.decision_makers);

  // ── Section 5 — Engagement & Sentiment ──
  setPass("lead_interest_level", lead.lead_interest_level);
  const sent = SENTIMENT_NORM(lead.sentiment || lead.overall_sentiment);
  if (sent) setPass("overall_sentiment", sent);
  setPass("pain_point_category", Array.isArray(lead.pain_points) ? lead.pain_points : (lead.pain_point_category ? lead.pain_point_category : null));
  setPass("engagement_level", lead.engagement_level);

  // ── Section 6 — Demo & Appointment ──
  const upcomingMeeting = Array.isArray(lead.meetings) ? lead.meetings.find(m => {
    try { return new Date(m.date || m.scheduled_at) >= new Date(); } catch { return false; }
  }) : null;
  if (upcomingMeeting) {
    setPass("demo_scheduled_date", upcomingMeeting.date || upcomingMeeting.scheduled_at);
    setPass("demo_type", upcomingMeeting.type);
  }

  // ── Section 7 — Next Actions & Ownership ──
  setPass("assigned_owner", lead.assignee || lead.assigned_owner || lead.owner);
  setPass("last_contact_date", lead.last_contact_date || lead.last_contacted);
  setPass("next_action", lead.next_action);

  // ── Section 8 — System & Tracking ──
  setPass("created_at", lead.created_at);
  setPass("updated_at", lead.updated_at);
  setPass("created_by", lead.created_by);

  return out;
}

/**
 * Mission-critical fields for the completeness pill / "Show missing" toggle.
 * @returns {Array<{key:string, label:string}>}
 */
export const MISSION_CRITICAL_FIELDS = [
  { key: "lead_stage", label: "Lead Stage" },
  { key: "lead_source", label: "Lead Source" },
  { key: "lead_rating", label: "Lead Rating" },
  { key: "specialty", label: "Specialty" },
  { key: "budget_range", label: "Budget Range" },
  { key: "phone", label: "Primary Phone" },
  { key: "pain_point_category", label: "Pain Point Category" },
  { key: "products_of_interest", label: "Products of Interest" },
];

/**
 * Every field key the Information tab tracks. Keep in sync with
 * LeadContactSections.jsx field rows. Used by the completeness pill.
 */
export const ALL_INFORMATION_FIELDS = [
  // Contact & Practice Identity (12)
  "contact_name","phone","email","company_name","organizational_role","practice_type",
  "specialty","time_zone","address_street","address_city","address_state","address_zip",
  // Pipeline & Lead Status (8)
  "lead_stage","sub_status","lead_source","lead_rating","pipeline","assignee_team",
  "assigned_owner","created_at",
  // Products & Qualification (14)
  "products_of_interest","feature_interest_category","key_value_proposition","provider_count",
  "ehr_in_use","billing_setup","telehealth_use","prospect_decision_timeline","reason_category","npi",
  "ehr_name","pms_vendor","other_vendor","vendor_type",
  // Budget & Deal Details (8)
  "budget_range","pricing_fit_status","est_deal_value","key_decision_makers_identified",
  "deal_close_probability","contract_term","payment_terms","discount_offered",
  // Engagement & Sentiment (10)
  "lead_interest_level","overall_sentiment","pain_point_category","engagement_level",
  "communication_preference","best_time_to_call","language_preference","website_visits",
  "emails_opened","content_downloads",
  // Demo & Appointment (8)
  "demo_scheduled_date","demo_type","demo_attendees","demo_outcome",
  "follow_up_date","follow_up_type","meeting_recorded","meeting_recording_url",
  // Next Actions & Ownership (12)
  "next_action","next_action_due","last_contact_date","last_contact_method",
  "last_call_status","total_call_attempts","callback_due","follow_up_priority",
  "tasks_open","tasks_overdue","reassignment_history","ownership_changes",
  // System & Tracking (20)
  "created_at","updated_at","created_by","modified_by","import_batch","data_source_quality",
  "duplicate_status","crm_link_id","external_lead_id","custom_tags",
  "do_not_call","do_not_email","gdpr_consent","marketing_consent",
  "internal_notes","competitive_intel","attached_files","linkedin_url",
  "twitter_handle","website_url",
];

/**
 * Computes how many fields on the lead are populated.
 * @param {object} lead - lead (post-prefill)
 * @returns {{filled:number, total:number, percent:number, missingRequired:string[]}}
 */
export function computeCompleteness(lead) {
  if (!lead) return { filled: 0, total: ALL_INFORMATION_FIELDS.length, percent: 0, missingRequired: MISSION_CRITICAL_FIELDS.map(f => f.label) };
  const isFilled = (v) => {
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  };
  const total = ALL_INFORMATION_FIELDS.length;
  const filled = ALL_INFORMATION_FIELDS.filter((k) => isFilled(lead[k])).length;
  const missingRequired = MISSION_CRITICAL_FIELDS
    .filter((f) => !isFilled(lead[f.key]))
    .map((f) => f.label);
  return {
    filled,
    total,
    percent: Math.round((filled / total) * 100),
    missingRequired,
  };
}
